import { N8N_BROADCAST_URL } from '@/api/n8n'
import type { PendingBroadcast, CreateBroadcastInput, BroadcastStatusRow, BroadcastStatusGroup } from '@/types/broadcast'

// Client próprio, não usa useAdminDB() (aquele é acoplado ao N8N_TEAMS_URL) —
// este recurso roda no workflow isolado "WootDash - MSG" (n8n/WootDash - MSG.json).
async function post(command: string, extra: Record<string, unknown> = {}): Promise<unknown> {
  const res = await fetch(N8N_BROADCAST_URL, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ command, timestamp: Date.now(), ...extra }),
  })
  if (!res.ok) throw new Error(`Broadcast ${res.status}`)
  const text = await res.text()
  if (!text.trim()) return []
  return JSON.parse(text)
}

function toEpoch(v: unknown): number {
  return Math.floor(new Date(String(v)).getTime() / 1000)
}

function rowToPending(row: Record<string, unknown>): PendingBroadcast {
  return {
    id:         Number(row.id),
    senderId:   Number(row.sender_id),
    body:       String(row.body ?? ''),
    imageUrl:   row.image_url ? String(row.image_url) : null,
    youtubeUrl: row.youtube_url ? String(row.youtube_url) : null,
    linkUrl:    row.link_url ? String(row.link_url) : null,
    createdAt:  toEpoch(row.created_at),
  }
}

export async function createBroadcast(input: CreateBroadcastInput): Promise<void> {
  await post('create_broadcast', {
    sender_id:     input.senderId,
    body:          input.body,
    image_url:     input.imageUrl || undefined,
    youtube_url:   input.youtubeUrl || undefined,
    link_url:      input.linkUrl || undefined,
    recipient_ids: input.recipientIds,
  })
}

// Polling do agente-alvo (useBroadcast.ts, a cada 15s) — avisos ainda sem acked_at.
export async function fetchPendingBroadcasts(userId: number): Promise<PendingBroadcast[]> {
  const data = await post('list_pending_broadcasts', { user_id: userId })
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map(rowToPending)
}

export async function ackBroadcast(broadcastId: number, userId: number): Promise<void> {
  await post('ack_broadcast', { broadcast_id: broadcastId, user_id: userId })
}

// sender_id omitido = todos os broadcasts (não só os enviados pelo próprio usuário).
export async function fetchBroadcastStatus(senderId?: number): Promise<BroadcastStatusRow[]> {
  const data = await post('list_broadcast_status', senderId != null ? { sender_id: senderId } : {})
  const rows = Array.isArray(data) ? data as Record<string, unknown>[] : []
  return rows.map((r): BroadcastStatusRow => ({
    broadcastId: Number(r.broadcast_id),
    senderId:    Number(r.sender_id),
    body:        String(r.body ?? ''),
    imageUrl:    r.image_url ? String(r.image_url) : null,
    youtubeUrl:  r.youtube_url ? String(r.youtube_url) : null,
    linkUrl:     r.link_url ? String(r.link_url) : null,
    createdAt:   toEpoch(r.created_at),
    userId:      Number(r.user_id),
    ackedAt:     r.acked_at ? toEpoch(r.acked_at) : null,
  }))
}

// Agrupa as linhas flat (1 por destinatário) por broadcast, pra tela de
// histórico (BroadcastsSection.vue) montar "quem confirmou / quem falta".
export function groupBroadcastStatus(rows: BroadcastStatusRow[]): BroadcastStatusGroup[] {
  const byId = new Map<number, BroadcastStatusGroup>()
  for (const r of rows) {
    let group = byId.get(r.broadcastId)
    if (!group) {
      group = {
        broadcastId: r.broadcastId, senderId: r.senderId, body: r.body,
        imageUrl: r.imageUrl, youtubeUrl: r.youtubeUrl, linkUrl: r.linkUrl,
        createdAt: r.createdAt, recipients: [],
      }
      byId.set(r.broadcastId, group)
    }
    group.recipients.push({ userId: r.userId, ackedAt: r.ackedAt })
  }
  return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt)
}
